/**
 * /b/:slug — brokerage branded landing page.
 *
 * Brokerages link to this page from their own websites ("Apply Now", "24/7
 * Assistance"). The slug identifies the tenant; the page shows the brokerage
 * name and routes the visitor to sign up or log in, scoped to that tenant.
 */

import { Building2, Loader2, LogIn, Phone, UserPlus } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";

import {
  inviteTokenFor,
  loadOnboardingAgent,
  saveOnboardingAgent,
} from "../home/onboardingAgent";
import type { AuthNavState } from "./AuthBrand";
import { ComplianceFooter } from "./ComplianceFooter";
import { IntakeForm } from "./IntakeForm";
import { identityApi, type TenantLookupResponse } from "./passkeyApi";
import "./SignIn.css";

type State =
  | { kind: "loading" }
  | { kind: "ready"; tenant: TenantLookupResponse }
  | { kind: "error"; message: string };

export function BrokeragePage({
  slug: slugProp,
  inPlace = false,
}: {
  /** Overrides the route's `:slug` when rendered in place at `/` (spec 011). */
  slug?: string;
  inPlace?: boolean;
} = {}) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug;
  const [state, setState] = useState<State>({ kind: "loading" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug) {
      setState({ kind: "error", message: "No brokerage slug in the URL." });
      return;
    }
    identityApi
      .getTenantBySlug(slug)
      .then((tenant) => {
        // When this is an agent's own landing (V060 domain/slug carries an
        // agent), stash them so the client lands assigned — the same carriage
        // the /join?ref invite uses. A brokerage's own landing carries no agent
        // and the client lands unassigned into the broker's action queue.
        if (tenant.agent_id) {
          saveOnboardingAgent({
            agentId: tenant.agent_id,
            agentTenantId: tenant.tenant_id,
            agentName: tenant.agent_name ?? undefined,
            // Spec 023: an invite from THIS brokerage stays with the visitor.
            inviteToken: inviteTokenFor(tenant.tenant_id),
          });
        } else {
          // An agentless page of ANOTHER brokerage: drop a leftover invite token so
          // it cannot pull the sign-up into the brokerage that issued it (spec 023).
          const stored = loadOnboardingAgent();
          if (
            stored?.inviteToken &&
            stored.agentTenantId !== tenant.tenant_id
          ) {
            const { inviteToken: _dropped, ...rest } = stored;
            saveOnboardingAgent(rest);
          }
        }
        setState({ kind: "ready", tenant });
      })
      .catch((err: unknown) =>
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : "Brokerage not found.",
        }),
      );
  }, [slug]);

  if (state.kind === "loading") {
    return (
      <SigninRoot inPlace={inPlace}>
        <section className="signin__card">
          <div className="signin__brand">
            <Loader2 className="signin__spin" size={28} aria-hidden />
            <span>Loading…</span>
          </div>
        </section>
      </SigninRoot>
    );
  }

  if (state.kind === "error") {
    // The domain resolved to no brokerage — never a dead end. Offer the intake
    // form so a human can follow up, rather than inventing a tenant for them.
    return (
      <SigninRoot inPlace={inPlace}>
        <section className="signin__card">
          <IntakeForm />
        </section>
      </SigninRoot>
    );
  }

  const { tenant } = state;
  // The brokerage's registered legal name (e.g. "... LLC") is the identity
  // TREC/NMLS actually require on a public page — display_name is a friendly
  // label that may drop it. Fall back to display_name only if the brokerage
  // profile (still being completed) hasn't got one yet.
  const brandName = tenant.brokerage?.legal_name ?? tenant.display_name;
  // Single brand: this is the brokerage's own page, not an agent's, so there is
  // no second licence in play (contrast AgentLandingPage).
  const authState: AuthNavState = {
    tenantId: tenant.tenant_id,
    brandName,
    brands: [{ name: brandName, logoUrl: tenant.brokerage?.logo_url ?? null }],
  };

  return (
    <SigninRoot inPlace={inPlace}>
      <section className="signin__card">
        <div
          className="signin__brand"
          style={{
            justifyContent: "center",
            marginBottom: "var(--rp-space-4)",
          }}
        >
          <BrandMark tenant={tenant} size={128} />
        </div>
        <h1
          style={{
            textAlign: "center",
            // A long name wraps (spec 013). "anywhere" also lets a single
            // over-long word break, and is what lets the grid column shrink.
            // The size stays inline: Modern's stylesheet asks for 32px, which
            // would break TREC's half-size rule for the footer's broker name.
            overflowWrap: "anywhere",
            fontSize: "clamp(1.1rem, 6vw, 1.5rem)",
          }}
        >
          Welcome to {brandName}
        </h1>
        {tenant.brokerage?.primary_phone && (
          <p className="signin__subtitle" style={{ textAlign: "center" }}>
            <a
              href={`tel:${tenant.brokerage.primary_phone}`}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Phone size={14} aria-hidden /> {tenant.brokerage.primary_phone}
            </a>
          </p>
        )}
        <p className="signin__subtitle" style={{ textAlign: "center" }}>
          Sign in to your client portal or create a new account.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            type="button"
            className="signin__cta rp-btn rp-btn--primary"
            onClick={() => void navigate("/sign-up", { state: authState })}
          >
            <UserPlus size={18} aria-hidden />
            Create account
          </button>
          <button
            type="button"
            className="signin__cta rp-btn"
            onClick={() => void navigate("/login", { state: authState })}
          >
            <LogIn size={18} aria-hidden />
            Sign in
          </button>
        </div>

        <p className="signin__footnote">
          Your account is secured with a passkey — no password to remember.
        </p>

        <ComplianceFooter tenant={tenant} />
      </section>
    </SigninRoot>
  );
}

/**
 * Tenant logo if uploaded (V002/PR A), else the generic building glyph. The
 * logo is decoration — it never stands in for BrokerIdentification, which
 * TREC/NMLS require on every public page regardless of branding.
 */
export function BrandMark({
  tenant,
  size = 28,
}: {
  tenant: TenantLookupResponse;
  /** Pixel size — default matches its inline use next to a name; the
   * standalone/centered use (BrokeragePage) passes a larger size so the
   * logo reads as the page's primary mark, bigger than the welcome text. */
  size?: number;
}) {
  const logoUrl = tenant.brokerage?.logo_url;
  if (!logoUrl) return <Building2 size={size} aria-hidden />;
  return (
    <img
      src={logoUrl}
      alt=""
      aria-hidden
      style={{
        height: size,
        width: size,
        objectFit: "contain",
        borderRadius: 4,
      }}
    />
  );
}

/**
 * The page's root. On its own route it is the page's `<main>`, under
 * AuthLayout's fixed, overlaid bar. Rendered in place at `/` it sits inside the
 * Shell, which already owns `<main>` and a sticky bar that takes up space — so
 * it is a plain element, sized for that bar (`.signin--in-place`).
 */
export function SigninRoot({
  inPlace,
  children,
}: {
  inPlace: boolean;
  children: ReactNode;
}) {
  return inPlace ? (
    <div className="signin signin--in-place">{children}</div>
  ) : (
    <main className="signin">{children}</main>
  );
}
